import React from "react";
import { Table, Badge, Button, Spinner, Alert, ProgressBar } from "react-bootstrap";
import { 
  IoEye, 
  IoCreate, 
  IoTrash, 
  IoCalendar, 
  IoCash, 
  IoLocation, 
  IoBusiness, 
  IoPeople,
  IoFlag,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoTime
} from "react-icons/io5";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

const CareerTable = ({
  careers = [],
  loading = false,
  error = null,
  pagination = {},
  meta = {},
  currentSortField = null,
  currentSortOrder = "DESC",
  onSort = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onView = () => {},
  onToggleUrgent = () => {},
  onToggleStatus = () => {},
}) => {

  // Format tanggal
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format gaji
  const formatSalary = (min, max) => {
    if (!min && !max) return "Negotiable";
    if (min && max) return `Rp ${formatNumber(min)} - Rp ${formatNumber(max)}`;
    if (min) return `Rp ${formatNumber(min)}+`;
    if (max) return `Up to Rp ${formatNumber(max)}`;
    return "Negotiable";
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // Hitung hari tersisa
  const calculateDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const timeDiff = deadlineDate.getTime() - today.getTime();
    const days = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return days;
  };

  // Render badge untuk status aktif
  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success" className="d-flex align-items-center gap-1">
        <IoCheckmarkCircle size={12} /> Active
      </Badge>
    ) : (
      <Badge bg="secondary" className="d-flex align-items-center gap-1">
        <IoCloseCircle size={12} /> Inactive
      </Badge>
    );
  };

  // Render badge untuk urgent status
  const getUrgentBadge = (isUrgent) => {
    return isUrgent ? (
      <Badge bg="danger" className="d-flex align-items-center gap-1">
        <IoFlag size={12} /> Urgent
      </Badge>
    ) : null;
  };

  // Render badge untuk employment type
  const getEmploymentTypeBadge = (type) => {
    const typeConfig = {
      "Full Time": { variant: "primary", label: "Full Time" },
      "Part Time": { variant: "info", label: "Part Time" },
      "Contract": { variant: "warning", label: "Contract" },
      "Internship": { variant: "success", label: "Internship" },
      "Freelance": { variant: "secondary", label: "Freelance" },
    };
    const config = typeConfig[type] || { variant: "light", label: type };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  // Render badge untuk position level
  const getPositionLevelBadge = (level) => {
    const levelConfig = {
      "Intern": { variant: "light", label: "Intern" },
      "Junior": { variant: "info", label: "Junior" },
      "Associate": { variant: "info", label: "Associate" },
      "Mid Level": { variant: "primary", label: "Mid Level" },
      "Senior": { variant: "success", label: "Senior" },
      "Lead": { variant: "warning", label: "Lead" },
      "Manager": { variant: "danger", label: "Manager" },
      "Director": { variant: "dark", label: "Director" },
      "Senior Specialist": { variant: "success", label: "Senior Specialist" },
    };
    const config = levelConfig[level] || { variant: "secondary", label: level };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  // Render badge untuk days remaining
  const getDaysRemainingBadge = (deadline) => {
    const days = calculateDaysRemaining(deadline);
    if (days === null) return <Badge bg="secondary">No Deadline</Badge>;
    
    if (days <= 0) {
      return (
        <Badge bg="danger" className="d-flex align-items-center gap-1">
          <IoTime size={12} /> Expired
        </Badge>
      );
    } else if (days <= 3) {
      return (
        <Badge bg="danger" className="d-flex align-items-center gap-1">
          <IoTime size={12} /> {days}d
        </Badge>
      );
    } else if (days <= 7) {
      return (
        <Badge bg="warning" className="d-flex align-items-center gap-1">
          <IoTime size={12} /> {days}d
        </Badge>
      );
    } else {
      return (
        <Badge bg="success" className="d-flex align-items-center gap-1">
          <IoTime size={12} /> {days}d
        </Badge>
      );
    }
  };

  // Render progress bar untuk applicants
  const renderApplicantsProgress = (applicants, positions) => {
    const maxPositions = Math.max(positions, applicants, 1);
    const percentage = (applicants / maxPositions) * 100;
    
    let variant = "success";
    if (percentage >= 100) variant = "danger";
    else if (percentage >= 75) variant = "warning";
    else if (percentage >= 50) variant = "info";
    
    return (
      <div className="d-flex align-items-center gap-2">
        <div style={{ width: "60px" }}>
          <ProgressBar 
            now={percentage} 
            variant={variant}
            style={{ height: "6px" }}
          />
        </div>
        <small className="fw-semibold">
          {applicants}/{positions}
        </small>
      </div>
    );
  };

  // Render icon sort
  const renderSortIcon = (field) => {
    if (currentSortField !== field) return <FaSort className="text-muted" />;
    return currentSortOrder === "ASC" ? <FaSortUp /> : <FaSortDown />;
  };

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading careers...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="danger" className="my-4">
        <Alert.Heading>Error Loading Data</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  // Empty state
  if (!loading && !error && careers.length === 0) {
    return (
      <div className="text-center py-5 border rounded">
        <div className="text-muted mb-2">No career opportunities found</div>
        {pagination.search && (
          <small className="text-muted">
            Try adjusting your search or filter criteria
          </small>
        )}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table hover className="align-middle">
        <thead className="table-light">
          <tr>
            <th style={{ width: "25%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold d-flex align-items-center gap-1"
                onClick={() => onSort("title")}
              >
                Position {renderSortIcon("title")}
              </Button>
            </th>
            <th style={{ width: "15%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold d-flex align-items-center gap-1"
                onClick={() => onSort("specialization")}
              >
                <IoBusiness size={14} /> Specialization {renderSortIcon("specialization")}
              </Button>
            </th>
            <th style={{ width: "15%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold d-flex align-items-center gap-1"
                onClick={() => onSort("location")}
              >
                <IoLocation size={14} /> Location {renderSortIcon("location")}
              </Button>
            </th>
            <th style={{ width: "10%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold d-flex align-items-center gap-1"
                onClick={() => onSort("salary_max")}
              >
                <IoCash size={14} /> Salary {renderSortIcon("salary_max")}
              </Button>
            </th>
            <th style={{ width: "15%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold d-flex align-items-center gap-1"
                onClick={() => onSort("application_deadline")}
              >
                <IoCalendar size={14} /> Deadline {renderSortIcon("application_deadline")}
              </Button>
            </th>
            <th style={{ width: "10%" }}>
              <span className="fw-semibold d-flex align-items-center gap-1">
                <IoPeople size={14} /> Status
              </span>
            </th>
            <th style={{ width: "10%" }} className="text-center">
              <span className="fw-semibold">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {careers.map((career) => {
            const daysRemaining = calculateDaysRemaining(career.application_deadline);
            const isExpired = daysRemaining !== null && daysRemaining <= 0;
            
            return (
              <tr 
                key={career.id} 
                className={isExpired ? "opacity-75" : ""}
              >
                <td>
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-start gap-2 mb-1">
                      <strong className="mb-0">{career.title}</strong>
                      {getUrgentBadge(career.is_urgent)}
                    </div>
                    <div className="d-flex flex-wrap gap-1 mb-1">
                      {getEmploymentTypeBadge(career.employment_type)}
                      {getPositionLevelBadge(career.position_level)}
                      {career.remote_allowed && (
                        <Badge bg="success">Remote</Badge>
                      )}
                    </div>
                    <small className="text-muted">
                      {career.required_skills?.slice(0, 2).map(skill => (
                        <span key={skill} className="me-1">#{skill}</span>
                      ))}
                      {career.required_skills?.length > 2 && (
                        <span className="text-muted">+{career.required_skills.length - 2} more</span>
                      )}
                    </small>
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column">
                    <span className="fw-medium">{career.specialization}</span>
                    <small className="text-muted">{career.department || "General"}</small>
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-1">
                    <IoLocation size={14} className="text-muted" />
                    <span>{career.location}</span>
                  </div>
                  {career.work_environment && (
                    <small className="text-muted d-block mt-1">
                      {career.work_environment}
                    </small>
                  )}
                </td>
                <td>
                  <div className="d-flex flex-column">
                    <span className="fw-semibold text-primary">
                      {formatSalary(career.salary_min, career.salary_max)}
                    </span>
                    {career.experience_required > 0 && (
                      <small className="text-muted">
                        {career.experience_required}+ yrs exp
                      </small>
                    )}
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-center gap-1 mb-1">
                      <IoCalendar size={14} className="text-muted" />
                      <span>{formatDate(career.application_deadline)}</span>
                    </div>
                    <div>
                      {getDaysRemainingBadge(career.application_deadline)}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column gap-2">
                    <div>
                      {getStatusBadge(career.is_active)}
                    </div>
                    <div>
                      {renderApplicantsProgress(
                        career.current_applicants, 
                        career.available_positions
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="d-flex justify-content-center gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      title="View Details"
                      onClick={() => onView(career)}
                      className="d-flex align-items-center gap-1"
                    >
                      <IoEye size={14} />
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      title="Edit Career"
                      onClick={() => onEdit(career)}
                      className="d-flex align-items-center gap-1"
                    >
                      <IoCreate size={14} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title="Delete Career"
                      onClick={() => onDelete(career.id, career.title)}
                      className="d-flex align-items-center gap-1"
                    >
                      <IoTrash size={14} />
                    </Button>
                  </div>
                  <div className="d-flex justify-content-center gap-1 mt-2">
                    {/* <Button
                      variant={career.is_urgent ? "danger" : "outline-danger"}
                      size="sm"
                      title={career.is_urgent ? "Remove Urgent" : "Mark as Urgent"}
                      onClick={() => onToggleUrgent(career.id, career.is_urgent)}
                      className="px-2"
                    >
                      <IoFlag size={12} />
                    </Button> */}
                    {/* <Button
                      variant={career.is_active ? "success" : "outline-secondary"}
                      size="sm"
                      title={career.is_active ? "Deactivate" : "Activate"}
                      onClick={() => onToggleStatus(career.id, career.is_active)}
                      className="px-2"
                    >
                      {career.is_active ? (
                        <IoCheckmarkCircle size={12} />
                      ) : (
                        <IoCloseCircle size={12} />
                      )}
                    </Button> */}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Pagination Info */}
      {meta && meta.totalItems > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3 px-2">
          <div className="text-muted small">
            Showing {((meta.currentPage - 1) * meta.itemsPerPage) + 1} to{" "}
            {Math.min(meta.currentPage * meta.itemsPerPage, meta.totalItems)} of{" "}
            {meta.totalItems} careers
          </div>
          <div className="text-muted small">
            Page {meta.currentPage} of {meta.totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerTable;